import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Bell, Send } from "lucide-react";

export const EmailNotificationSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [preferences, setPreferences] = useState({
    id: '',
    is_enabled: true,
    email_address: '',
    new_conversation: true,
    conversation_assigned: true,
    customer_reply: true,
    conversation_resolved: false,
    daily_summary: false,
    weekly_summary: false,
  });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('email_notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (!data) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('email')
          .eq('id', user.id)
          .single();

        const { data: newPrefs, error: createError } = await supabase
          .from('email_notification_preferences')
          .insert({
            user_id: user.id,
            email_address: profile?.email || '',
            is_enabled: true,
            new_conversation: true,
            conversation_assigned: true,
            customer_reply: true,
            conversation_resolved: false,
            daily_summary: false,
            weekly_summary: false,
          })
          .select()
          .single();

        if (createError) throw createError;
        setPreferences(newPrefs);
      } else {
        setPreferences(data);
      }
    } catch (error: any) {
      toast({
        title: "Error loading preferences",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('email_notification_preferences')
        .update({
          is_enabled: preferences.is_enabled,
          email_address: preferences.email_address,
          new_conversation: preferences.new_conversation,
          conversation_assigned: preferences.conversation_assigned,
          customer_reply: preferences.customer_reply,
          conversation_resolved: preferences.conversation_resolved,
          daily_summary: preferences.daily_summary,
          weekly_summary: preferences.weekly_summary,
        })
        .eq('id', preferences.id);

      if (error) throw error;

      toast({
        title: "Preferences saved",
        description: "Your email notification preferences have been updated.",
      });
    } catch (error: any) {
      toast({
        title: "Error saving preferences",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setSendingTest(true);
    try {
      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          to: preferences.email_address,
          template_key: 'test_notification',
          variables: {
            user_name: 'Test User',
          },
        },
      });

      if (error) throw error;

      toast({
        title: "Test email sent",
        description: `A test notification has been sent to ${preferences.email_address}`,
      });
    } catch (error: any) {
      toast({
        title: "Error sending test email",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          <div>
            <CardTitle>Email Notifications</CardTitle>
            <CardDescription>Manage your email notification preferences</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="email-address">Notification Email Address</Label>
          <Input
            id="email-address"
            type="email"
            value={preferences.email_address}
            onChange={(e) => setPreferences({ ...preferences, email_address: e.target.value })}
            placeholder="your.email@example.com"
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="notifications-enabled" className="font-semibold">Enable All Email Notifications</Label>
          <Switch
            id="notifications-enabled"
            checked={preferences.is_enabled}
            onCheckedChange={(checked) => setPreferences({ ...preferences, is_enabled: checked })}
          />
        </div>

        {preferences.is_enabled && (
          <div className="space-y-4 pl-4 border-l-2 border-border">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="new-conversation">New Conversations</Label>
                <p className="text-sm text-muted-foreground">When a new conversation starts</p>
              </div>
              <Switch
                id="new-conversation"
                checked={preferences.new_conversation}
                onCheckedChange={(checked) => setPreferences({ ...preferences, new_conversation: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="conversation-assigned">Conversation Assigned</Label>
                <p className="text-sm text-muted-foreground">When a conversation is assigned to you</p>
              </div>
              <Switch
                id="conversation-assigned"
                checked={preferences.conversation_assigned}
                onCheckedChange={(checked) => setPreferences({ ...preferences, conversation_assigned: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="customer-reply">Customer Replies</Label>
                <p className="text-sm text-muted-foreground">When a customer replies to your conversation</p>
              </div>
              <Switch
                id="customer-reply"
                checked={preferences.customer_reply}
                onCheckedChange={(checked) => setPreferences({ ...preferences, customer_reply: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="conversation-resolved">Conversation Resolved</Label>
                <p className="text-sm text-muted-foreground">When your conversation is resolved</p>
              </div>
              <Switch
                id="conversation-resolved"
                checked={preferences.conversation_resolved}
                onCheckedChange={(checked) => setPreferences({ ...preferences, conversation_resolved: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="daily-summary">Daily Summary</Label>
                <p className="text-sm text-muted-foreground">Daily stats at 9 AM (coming soon)</p>
              </div>
              <Switch
                id="daily-summary"
                checked={preferences.daily_summary}
                onCheckedChange={(checked) => setPreferences({ ...preferences, daily_summary: checked })}
                disabled
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="weekly-summary">Weekly Summary</Label>
                <p className="text-sm text-muted-foreground">Weekly stats every Monday at 9 AM (coming soon)</p>
              </div>
              <Switch
                id="weekly-summary"
                checked={preferences.weekly_summary}
                onCheckedChange={(checked) => setPreferences({ ...preferences, weekly_summary: checked })}
                disabled
              />
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Preferences
          </Button>
          <Button 
            variant="outline" 
            onClick={handleTestEmail} 
            disabled={sendingTest || !preferences.email_address || !preferences.is_enabled}
          >
            {sendingTest ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Send Test Email
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
